import { GARMENTS_POLICY_ID } from '~~/shared/garments'
import { BADGE_POLICY_ID } from '~~/shared/badge'
import { DMP_POLICY_ID } from '~~/shared/dmp-access'
import { HR_POLICY_ID } from '~~/shared/hr-access'
import { RECRUITMENT_POLICY_ID } from '~~/shared/recruitment'
import { DISPATCH_MANAGER_POLICY_ID, DISPATCH_MEMBER_POLICY_ID, DISPATCH_ON_CALL_POLICY_ID } from '~~/shared/dispatch'

export const ROLE_IDS = {
  ADMIN: 'fb6e3b9d-574c-4242-9cd4-9c6379ba729d',
  VOLONTAIRE: '3e9514b5-8083-4e7b-8504-2f1e893ab6e6',
} as const

// Page du tunnel de configuration 2FA (seule page accessible tant que la 2FA obligatoire n'est pas configurée)
export const TFA_SETUP_PATH = '/volontaires/securite/2fa'

export const POLICY_IDS = {
  CA_MEMBER: 'a131480f-cda7-46ae-8e1c-98159a8d936c',
  SERVICE_MANAGER: 'bcc67305-7e37-454e-a05d-36df5d7b4790',
  FORMATION_MANAGER: 'ab3cddf1-eefd-4a1a-b2a5-45ea854f6953',
  GARMENTS_MANAGER: GARMENTS_POLICY_ID,
  BADGE_MANAGER: BADGE_POLICY_ID,
  DMP_MANAGER: DMP_POLICY_ID,
  HR_MANAGER: HR_POLICY_ID,
  RECRUITMENT_MANAGER: RECRUITMENT_POLICY_ID,
  DISPATCH_MEMBER: DISPATCH_MEMBER_POLICY_ID,
  DISPATCH_MANAGER: DISPATCH_MANAGER_POLICY_ID,
  DISPATCH_ON_CALL: DISPATCH_ON_CALL_POLICY_ID,
} as const

export type MenuKey
  = 'home'
    | 'shop'
    | 'orders'
    | 'returns'
    | 'garments'
    | 'dispatching'
    | 'directory'
    | 'documents'
    | 'support'
    | 'settings'
    | 'ebrigade'
    | 'mail'
    | 'backToSite'
    | 'adminUsers'
    | 'adminUsersList'
    | 'adminUsersCreate'
    | 'adminOrders'
    | 'adminOrdersList'
    | 'adminOrdersCreate'
    | 'adminShop'
    | 'adminReturns'
    | 'adminTenues'
    | 'adminTenuesStock'
    | 'adminTenuesReturns'
    | 'adminTenuesTypes'
    | 'adminTenuesVolunteers'
    | 'adminTenuesInventory'
    | 'adminTenuesDepartures'
    | 'adminTenuesStats'
    | 'adminFormations'
    | 'adminFormationsList'
    | 'adminFormationsCreate'
    | 'adminDmp'
    | 'adminRecruitment'
    | 'adminFibam'
    | 'adminDmpDocuments'
    | 'adminLogistique'
    | 'adminLogistiqueDashboard'
    | 'adminLogistiqueTelecomList'
    | 'adminLogistiqueBadgeGenerator'
    | 'adminCa'
    | 'adminCaPoints'
    | 'adminDispatchPlanning'
    | 'adminDispatchEquity'
    | 'adminDispatchCoverage'
    | 'adminDispatchMonth'
    | 'adminDispatchSettings'
    | 'adminDispatchDepartures'
    | 'adminDispatchOnCall'
    | 'adminDispatchPhone'
    | 'adminDispatchDuty'
    | 'dispatchingOnCall'
export type DashboardRouteKey
  = | 'home'
    | 'shop'
    | 'orders'
    | 'garments'
    | 'directory'
    | 'settings'
    | 'support'
    | 'adminUsers'
    | 'adminOrders'
    | 'adminOrdersCreate'
    | 'adminShop'
    | 'adminFormations'
    | 'adminDmp'
    | 'adminRecruitment'
    | 'adminLogistique'
    | 'adminLogistiqueBadge'
    | 'adminTenues'
    | 'adminCa'
    | 'dispatching'
    | 'adminDispatch'
export type RoleLike = string | { id?: string } | null | undefined
export type PolicyLike = string | { id?: string } | null | undefined

type PermissionPatch<TKey extends string> = {
  add?: readonly TKey[]
  remove?: readonly TKey[]
  replace?: readonly TKey[]
}

type RolePolicy = {
  menu?: PermissionPatch<MenuKey>
  route?: PermissionPatch<DashboardRouteKey>
}

type AccessContext = {
  roleId: string
  policyIds: readonly string[]
}

const applyPermissionPatch = <TKey extends string>(
  basePermissions: readonly TKey[],
  patch?: PermissionPatch<TKey>,
): ReadonlySet<TKey> => {
  if (!patch)
    return new Set<TKey>(basePermissions)

  if (patch.replace)
    return new Set<TKey>(patch.replace)

  const next = new Set<TKey>(basePermissions)
  patch.add?.forEach(permission => next.add(permission))
  patch.remove?.forEach(permission => next.delete(permission))
  return next
}

// Permissions par défaut applicables à tous les rôles.
const BASE_MENU_PERMISSIONS: readonly MenuKey[] = [
  'home',
  'shop',
  'orders',
  'returns',
  'garments',
  'directory',
  'documents',
  'support',
  'settings',
  'ebrigade',
  'mail',
  'backToSite',
]

const BASE_ROUTE_PERMISSIONS: readonly DashboardRouteKey[] = [
  'home',
  'shop',
  'orders',
  'garments',
  'directory',
  'settings',
  'support',
]

// Source de vérité unique des exceptions par rôle.
const ROLE_POLICIES: Record<string, RolePolicy> = {
  [ROLE_IDS.ADMIN]: {
    menu: {
      add: [
        'adminUsers',
        'adminUsersList',
        'adminUsersCreate',
        'adminOrders',
        'adminOrdersList',
        'adminOrdersCreate',
        'adminShop',
        'adminReturns',
        'adminTenues',
        'adminTenuesStock',
        'adminTenuesReturns',
        'adminTenuesTypes',
        'adminTenuesVolunteers',
        'adminTenuesInventory',
        'adminTenuesDepartures',
        'adminTenuesStats',
        'adminFormations',
        'adminFormationsList',
        'adminFormationsCreate',
        'adminDmp',
        'adminRecruitment',
        'adminFibam',
        'adminDmpDocuments',
        'adminLogistique',
        'adminLogistiqueDashboard',
        'adminLogistiqueTelecomList',
        'adminLogistiqueBadgeGenerator',
        'adminCa',
        'adminCaPoints',
        'dispatching',
        'adminDispatchPlanning',
        'adminDispatchEquity',
        'adminDispatchCoverage',
        'adminDispatchSettings',
        'adminDispatchDepartures',
        'adminDispatchOnCall',
        'adminDispatchPhone',
        'dispatchingOnCall',
      ],
    },
    route: {
      add: [
        'adminUsers',
        'adminOrders',
        'adminOrdersCreate',
        'adminShop',
        'adminFormations',
        'adminDmp',
        'adminRecruitment',
        'adminLogistique',
        'adminLogistiqueBadge',
        'adminTenues',
        'adminCa',
        'dispatching',
        'adminDispatch',
      ],
    },
  },
  [ROLE_IDS.VOLONTAIRE]: {},
}

const POLICY_POLICIES: Record<string, RolePolicy> = {
  [POLICY_IDS.CA_MEMBER]: {
    menu: {
      add: [
        'adminCa',
        'adminCaPoints',
      ],
    },
    route: {
      add: [
        'adminCa',
      ],
    },
  },
  [POLICY_IDS.SERVICE_MANAGER]: {
    menu: {
      add: [
        'adminCa',
        'adminCaPoints',
      ],
    },
    route: {
      add: [
        'adminCa',
      ],
    },
  },
  [POLICY_IDS.GARMENTS_MANAGER]: {
    menu: {
      add: [
        'adminTenues',
        'adminTenuesStock',
        'adminTenuesReturns',
        'adminTenuesTypes',
        'adminTenuesVolunteers',
        'adminTenuesInventory',
        'adminTenuesDepartures',
        'adminTenuesStats',
      ],
    },
    route: {
      add: [
        'adminTenues',
        'adminOrdersCreate',
      ],
    },
  },
  [POLICY_IDS.BADGE_MANAGER]: {
    menu: {
      add: [
        'adminLogistiqueBadgeGenerator',
      ],
    },
    route: {
      add: [
        'adminLogistiqueBadge',
      ],
    },
  },
  [POLICY_IDS.DMP_MANAGER]: {
    menu: {
      add: [
        'adminDmp',
        'adminFibam',
        'adminDmpDocuments',
      ],
    },
    route: {
      add: [
        'adminDmp',
      ],
    },
  },
  [POLICY_IDS.HR_MANAGER]: {
    menu: {
      add: [
        'adminUsers',
        'adminUsersList',
        'adminRecruitment',
      ],
    },
    route: {
      add: [
        'adminUsers',
        'adminRecruitment',
      ],
    },
  },
  [POLICY_IDS.RECRUITMENT_MANAGER]: {
    menu: {
      add: [
        'adminRecruitment',
      ],
    },
    route: {
      add: [
        'adminRecruitment',
      ],
    },
  },
  [POLICY_IDS.DISPATCH_MEMBER]: {
    menu: { add: ['dispatching'] },
    route: { add: ['dispatching'] },
  },
  [POLICY_IDS.DISPATCH_MANAGER]: {
    menu: { add: ['dispatching', 'adminDispatchPlanning', 'adminDispatchEquity', 'adminDispatchCoverage', 'adminDispatchSettings', 'adminDispatchOnCall', 'adminDispatchPhone'] },
    route: { add: ['dispatching', 'adminDispatch'] },
  },
  [POLICY_IDS.DISPATCH_ON_CALL]: {
    menu: { add: ['dispatchingOnCall', 'adminDispatchPhone'] },
    route: { add: ['dispatching', 'adminDispatch'] },
  },
  [POLICY_IDS.FORMATION_MANAGER]: {
    menu: {
      add: [
        'adminFormationsList',
        'adminFormationsCreate',
      ],
    },
    route: {
      add: [
        'adminFormations',
      ],
    },
  },
}

const DEFAULT_MENU_PERMISSION_SET: ReadonlySet<MenuKey> = new Set(BASE_MENU_PERMISSIONS)
const DEFAULT_ROUTE_PERMISSION_SET: ReadonlySet<DashboardRouteKey> = new Set(BASE_ROUTE_PERMISSIONS)

const menuPermissionSetByRoleId: Record<string, ReadonlySet<MenuKey>> = Object.fromEntries(
  Object.entries(ROLE_POLICIES).map(([roleId, policy]) => [roleId, applyPermissionPatch(BASE_MENU_PERMISSIONS, policy.menu)]),
)

const routePermissionSetByRoleId: Record<string, ReadonlySet<DashboardRouteKey>> = Object.fromEntries(
  Object.entries(ROLE_POLICIES).map(([roleId, policy]) => [roleId, applyPermissionPatch(BASE_ROUTE_PERMISSIONS, policy.route)]),
)

const menuPermissionSetByPolicyId: Record<string, ReadonlySet<MenuKey>> = Object.fromEntries(
  Object.entries(POLICY_POLICIES).map(([policyId, policy]) => [policyId, applyPermissionPatch([], policy.menu)]),
)

const routePermissionSetByPolicyId: Record<string, ReadonlySet<DashboardRouteKey>> = Object.fromEntries(
  Object.entries(POLICY_POLICIES).map(([policyId, policy]) => [policyId, applyPermissionPatch([], policy.route)]),
)

const menuPermissionSetByContextCache = new Map<string, ReadonlySet<MenuKey>>()
const routePermissionSetByContextCache = new Map<string, ReadonlySet<DashboardRouteKey>>()

const segmentToRouteKey: Record<string, DashboardRouteKey> = {
  boutique: 'shop',
  commandes: 'orders',
  tenues: 'garments',
  dispatching: 'dispatching',
  annuaire: 'directory',
  parametres: 'settings',
  support: 'support',
}

const adminSegmentToRouteKey: Record<string, DashboardRouteKey> = {
  'utilisateurs': 'adminUsers',
  'commandes': 'adminOrders',
  'boutique': 'adminShop',
  'formations': 'adminFormations',
  'dmp': 'adminDmp',
  'recrutement': 'adminRecruitment',
  'fibam': 'adminDmp',
  'logistique': 'adminLogistique',
  'tenues': 'adminTenues',
  'points-ca': 'adminCa',
  'dispatching': 'adminDispatch',
}

const normalizePath = (path: string): string => {
  const [pathname] = path.split(/[?#]/)
  if (!pathname)
    return '/'

  if (pathname.length > 1 && pathname.endsWith('/'))
    return pathname.slice(0, -1)

  return pathname
}

export const resolveRoleId = (value: RoleLike): string => {
  if (typeof value === 'string' && value)
    return value

  if (value && typeof value === 'object' && value.id)
    return value.id

  return ROLE_IDS.VOLONTAIRE
}

const extractPolicyId = (value: PolicyLike): string | null => {
  if (typeof value === 'string' && value)
    return value

  if (value && typeof value === 'object') {
    // Format Directus policies.* : { id: "<junction-id>", policy: "<policy-id>", ... }
    const record = value as Record<string, unknown>
    if (typeof record.policy === 'string' && record.policy)
      return record.policy

    if (value.id)
      return value.id
  }

  return null
}

const sanitizePolicyIds = (policyIds: readonly string[]): string[] => {
  return Array.from(new Set(policyIds.filter(Boolean))).sort()
}

export const resolvePolicyIds = (value: unknown): string[] => {
  if (!value)
    return []

  if (Array.isArray(value)) {
    return sanitizePolicyIds(value.map(item => extractPolicyId(item as PolicyLike)).filter((item): item is string => Boolean(item)))
  }

  if (typeof value === 'object') {
    const record = value as { policies?: unknown }
    return resolvePolicyIds(record.policies)
  }

  return []
}

const buildAccessContextCacheKey = (roleId: string, policyIds: readonly string[]): string => {
  return `${roleId}::${sanitizePolicyIds(policyIds).join(',')}`
}

const getMenuPermissionSetByContext = (context: AccessContext): ReadonlySet<MenuKey> => {
  const cacheKey = buildAccessContextCacheKey(context.roleId, context.policyIds)
  const cached = menuPermissionSetByContextCache.get(cacheKey)
  if (cached)
    return cached

  const merged = new Set(getMenuPermissionSetByRoleId(context.roleId))
  if (context.roleId !== ROLE_IDS.ADMIN) {
    for (const policyId of sanitizePolicyIds(context.policyIds)) {
      const policyPermissions = menuPermissionSetByPolicyId[policyId]
      if (!policyPermissions)
        continue

      for (const permission of policyPermissions)
        merged.add(permission)
    }
  }

  menuPermissionSetByContextCache.set(cacheKey, merged)
  return merged
}

const getRoutePermissionSetByContext = (context: AccessContext): ReadonlySet<DashboardRouteKey> => {
  const cacheKey = buildAccessContextCacheKey(context.roleId, context.policyIds)
  const cached = routePermissionSetByContextCache.get(cacheKey)
  if (cached)
    return cached

  const merged = new Set(routePermissionSetByRoleId[context.roleId] ?? DEFAULT_ROUTE_PERMISSION_SET)
  if (context.roleId !== ROLE_IDS.ADMIN) {
    for (const policyId of sanitizePolicyIds(context.policyIds)) {
      const policyPermissions = routePermissionSetByPolicyId[policyId]
      if (!policyPermissions)
        continue

      for (const permission of policyPermissions)
        merged.add(permission)
    }
  }

  routePermissionSetByContextCache.set(cacheKey, merged)
  return merged
}

export const canAccessMenu = (role: RoleLike, menuKey: MenuKey): boolean => {
  return canAccessMenuByRoleId(resolveRoleId(role), menuKey)
}

const getMenuPermissionSetByRoleId = (roleId: string): ReadonlySet<MenuKey> => {
  return menuPermissionSetByRoleId[roleId] ?? DEFAULT_MENU_PERMISSION_SET
}

export const canAccessMenuByRoleId = (roleId: string, menuKey: MenuKey): boolean => {
  return getMenuPermissionSetByRoleId(roleId).has(menuKey)
}

export const canAccessMenuByContext = (roleId: string, policyIds: readonly string[], menuKey: MenuKey): boolean => {
  return getMenuPermissionSetByContext({ roleId, policyIds }).has(menuKey)
}

export const getMenuAccessChecker = (role: RoleLike) => {
  const roleId = resolveRoleId(role)
  return getMenuAccessCheckerByRoleId(roleId)
}

export const getMenuAccessCheckerByRoleId = (roleId: string) => {
  const permissionSet = getMenuPermissionSetByRoleId(roleId)
  return (menuKey: MenuKey) => permissionSet.has(menuKey)
}

export const getMenuAccessCheckerByContext = (roleId: string, policyIds: readonly string[]) => {
  const permissionSet = getMenuPermissionSetByContext({ roleId, policyIds })
  return (menuKey: MenuKey) => permissionSet.has(menuKey)
}

const toRouteKey = (path: string): DashboardRouteKey | null => {
  const normalizedPath = normalizePath(path)
  if (normalizedPath === '/volontaires')
    return 'home'

  if (!normalizedPath.startsWith('/volontaires/'))
    return null

  const [, , segment, adminSegment] = normalizedPath.split('/')

  if (!segment)
    return null

  if (segment === 'admin') {
    if (!adminSegment)
      return null

    if (adminSegment === 'commandes' && normalizedPath === '/volontaires/admin/commandes/creer')
      return 'adminOrdersCreate'

    if (adminSegment === 'logistique' && normalizedPath.startsWith('/volontaires/admin/logistique/badge'))
      return 'adminLogistiqueBadge'

    return adminSegmentToRouteKey[adminSegment] ?? null
  }

  return segmentToRouteKey[segment] ?? null
}

export const canAccessDashboardPath = (role: RoleLike, path: string): boolean => {
  return canAccessDashboardPathByRoleId(resolveRoleId(role), path)
}

export const canAccessDashboardPathByRoleId = (roleId: string, path: string): boolean => {
  return canAccessDashboardPathByContext(roleId, [], path)
}

export const canAccessDashboardPathByContext = (roleId: string, policyIds: readonly string[], path: string): boolean => {
  const normalizedPath = normalizePath(path)
  const routeKey = toRouteKey(path)
  if (!routeKey) {
    if (normalizedPath.startsWith('/volontaires/admin'))
      return roleId === ROLE_IDS.ADMIN

    return true
  }

  const allowed = getRoutePermissionSetByContext({ roleId, policyIds })
  return allowed.has(routeKey)
}
